import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";
import {
  APARTMENT_POLICIES,
  RENTAL_REQUIREMENTS,
  money,
  parseApartmentUnits,
} from "../utils/rentalPortal";

export default function TenantApartmentDetails() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get("/public/vacant-properties");
        const parsed = parseApartmentUnits(response.data);
        setUnit(parsed.find((item) => String(item.id) === String(unitId)) || null);
      } catch (error) {
        console.error(error);
        setUnit(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [unitId]);

  const requirements = useMemo(
    () => (unit?.rental_requirements?.length ? unit.rental_requirements : RENTAL_REQUIREMENTS),
    [unit]
  );
  const policies = useMemo(
    () => (unit?.apartment_policies?.length ? unit.apartment_policies : APARTMENT_POLICIES),
    [unit]
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/92" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {loading ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            Loading apartment details...
          </div>
        ) : !unit ? (
          <div className="rounded-xl border border-dashed border-white/20 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            Apartment unit not found.
          </div>
        ) : (
          <>
            <section className="mb-6 rounded-xl border border-white/10 bg-slate-950/92 p-6 shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-300">Tenant Units</p>
              <h1 className="mt-2 text-3xl font-bold text-white">Unit Details</h1>
              <p className="mt-2 text-sm text-slate-300">Review details and continue to rental application.</p>
            </section>

            <section className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
              <article className="rounded-xl border border-white/10 bg-slate-950/92 p-5 shadow-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-300">Apartment Unit</p>
                <h2 className="mt-2 text-3xl font-bold text-white">{unit.apartment_name}</h2>
                <p className="mt-2 text-sm text-slate-300">{unit.location}</p>
                <p className="mt-3 text-2xl font-bold text-cyan-200">{money(unit.monthly_rent)}</p>
                <p className="mt-1 text-sm text-slate-300">Availability Status: {unit.availability_status}</p>
                <p className="mt-3 text-sm leading-7 text-slate-300">{unit.description}</p>
              </article>

              <aside className="rounded-xl border border-white/10 bg-slate-950/92 p-4 shadow-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Actions</p>
                <div className="mt-3 grid gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/apply/${unit.id}`)}
                    className="rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-500"
                  >
                    Apply to Rent
                  </button>
                  <Link
                    to={`/contact?apartmentId=${unit.id}`}
                    className="rounded-lg border border-white/15 px-4 py-2.5 text-center text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                  >
                    Inquire Now
                  </Link>
                  <Link
                    to="/tenant/apartments"
                    className="rounded-lg border border-white/15 px-4 py-2.5 text-center text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                  >
                    Back to Apartments
                  </Link>
                </div>
              </aside>
            </section>

            <section className="grid gap-4 xl:grid-cols-2">
              <article className="rounded-xl border border-white/10 bg-slate-950/92 p-5 shadow-2xl">
                <h2 className="text-xl font-bold text-white">Rental Requirements</h2>
                <ul className="mt-3 space-y-2 text-sm text-slate-200">
                  {requirements.map((item, index) => (
                    <li key={`requirement-${index}`} className="rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2">
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
              <article className="rounded-xl border border-white/10 bg-slate-950/92 p-5 shadow-2xl">
                <h2 className="text-xl font-bold text-white">Apartment Rules and Policies</h2>
                <ul className="mt-3 space-y-2 text-sm text-slate-200">
                  {policies.map((item, index) => (
                    <li key={`policy-${index}`} className="rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2">
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
